import { useLocation, useParams } from "react-router-dom";
import buildRoute from "components/buildRoute";
import PageHeader from "components/PageHeader";
import { canAdminister } from "../hartUtils";
import { useModelDetails } from "../hartHooks";
import { Button, Box, Paper } from "@mui/material";
import AnalystFormFields, { validateAnalyst, analystNullableFields } from "../components/AnalystFormFields";
import AssociationsSection from "../components/associations";

// ========================================
// MODEL CONFIG
// ========================================
const MODEL_CONFIG = {
  apiEndpoint: '/hart/analysts',
  idParamName: 'analystId',
  displayName: 'Analyst',
  displayField: 'name',
  FormComponent: AnalystFormFields,
  dataKey: 'analyst',
  validateFn: validateAnalyst,
  nullableFields: analystNullableFields,
};
// ========================================

export const AnalystDetailsPage = () => {
  const { projectId, [MODEL_CONFIG.idParamName]: idParam } = useParams();
  const location = useLocation();

  const {
    data,
    setData,
    editable,
    submitting,
    loading,
    error,
    validationErrors,
    handleSubmit,
    handleEditToggle,
    handleBack,
  } = useModelDetails({
    apiEndpoint: MODEL_CONFIG.apiEndpoint,
    idParam,
    initialData: location.state?.[MODEL_CONFIG.dataKey],
    validateFn: MODEL_CONFIG.validateFn,
    nullableFields: MODEL_CONFIG.nullableFields,
  });

  const canEditRole = canAdminister(projectId);
  const { FormComponent, displayName, displayField } = MODEL_CONFIG;

  if (loading) return <p>Loading {displayName} details...</p>;
  if (error) return <p>Error loading {displayName}: {error}</p>;

  return (
    <>
      <PageHeader
        title={`${displayName}: ${displayField ? (data[displayField] || idParam) : idParam}`}
        action={
          canEditRole && (
            <Button
              variant="contained"
              color={editable ? "secondary" : "primary"}
              aria-label={`Edit ${displayName}`}
              onClick={handleEditToggle}
              sx={{ '&:hover': { backgroundColor: 'secondary.main' } }}
            >
              {editable ? "Cancel Edit" : `Edit ${displayName}`}
            </Button>
          )
        }
      />
      <Paper sx={{ p: 3, mb: 3, mt: 5 }}>
        <FormComponent data={data} editable={false} onChange={setData} errors={validationErrors} displayName={displayName} />
      </Paper>

      {!loading && data && data.id && (
        <AssociationsSection
          parentId={String(data.id)}
          parentData={data}
          parentType="analyst"
          associations={['jobs']}
          editable={editable}
          editableAssociationCallbacks={{
            jobs: (jobs) => setData(prev => ({
              ...prev,
              new_jobs: jobs,
            })),
          }}
        />
      )}

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="outlined" onClick={handleBack}>
          Back
        </Button>
        {editable && canEditRole && (
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Saving..." : "Submit"}
          </Button>
        )}
      </Box>
    </>
  );
};

const routeArgs = {
  element: <AnalystDetailsPage />,
};

export default buildRoute(routeArgs);
